import { useState, useEffect } from 'react';
import type { TenantUserRole } from '@acitra/shared';
import type { TenantUserWithDetails } from '../services/api';

interface ChangeRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newRole: TenantUserRole) => Promise<void>;
  user: TenantUserWithDetails;
  canPromoteToAdmin: boolean;
}

const ROLE_LABELS: Record<TenantUserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
};

export function ChangeRoleModal({
  isOpen,
  onClose,
  onConfirm,
  user,
  canPromoteToAdmin,
}: ChangeRoleModalProps) {
  const [newRole, setNewRole] = useState<TenantUserRole>(user.role);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setNewRole(user.role);
      setError(null);
      setIsUpdating(false);
    }
  }, [isOpen, user.role]);

  const handleConfirm = async () => {
    if (newRole === user.role) {
      onClose();
      return;
    }

    setIsUpdating(true);
    setError(null);

    try {
      await onConfirm(newRole);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role');
      setIsUpdating(false);
    }
  };

  if (!isOpen) return null;

  const availableRoles: TenantUserRole[] = canPromoteToAdmin ? ['admin', 'member'] : ['member'];

  // If demoting from admin and can't promote to admin, only show member
  if (user.role === 'admin' && !canPromoteToAdmin) {
    availableRoles.length = 0;
    availableRoles.push('member');
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform rounded-lg bg-white p-6 shadow-xl transition-all">
          {/* Header */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <svg
                  className="h-6 w-6 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Change Role</h3>
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            <p className="text-sm text-gray-600 mb-4">
              Change the role for <span className="font-semibold">{user.name || user.email}</span>
            </p>

            <p className="text-sm text-gray-500 mb-2">
              Current role:{' '}
              <span className="font-medium text-gray-900">{ROLE_LABELS[user.role]}</span>
            </p>

            <div className="space-y-2">
              {availableRoles.map((role) => (
                <label
                  key={role}
                  className={`flex items-center p-3 border rounded-md cursor-pointer transition-colors ${
                    newRole === role
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={role}
                    checked={newRole === role}
                    onChange={(e) => setNewRole(e.target.value as TenantUserRole)}
                    className="h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                  />
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">{ROLE_LABELS[role]}</span>
                    <p className="text-xs text-gray-500">
                      {role === 'admin'
                        ? 'Can invite members, manage sync settings, and view team'
                        : 'Can view and upload invoices'}
                    </p>
                  </div>
                </label>
              ))}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isUpdating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={newRole === user.role || isUpdating}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUpdating ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Updating...
                </span>
              ) : (
                'Update Role'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
